// Parcours « Compose ton lot » : taille → 3 rabats → accessoires → récapitulatif.
// Porté depuis la maquette validée par Theo (claude.ai/artifact/4s8K1VgM1iPNqozFidq7eS) ;
// garder les deux alignés tant que la maquette sert de référence.
// root : ShadowRoot du widget ; wz : conteneur ; DATA : voir buildWizardData() ; hooks : scrollTop, headerH, addToCart.
// Tous les textes passent par DATA.i18n (voir i18n.js) : t(clé, variables), n(pluriel), money(centimes).
export function runWizard(root, wz, DATA, hooks) {
  const D = DATA.discount;
  const { t, n: tn } = DATA.i18n;
  const fmt = DATA.i18n.money;
  const PCT = `−${D} %`;
  const pctHTML = `<span style="color:var(--accent);white-space:nowrap">${PCT}</span>`;
  const off = c => Math.round(c * (1 - D / 100));
  // Prix barré + prix remisé, pour montrer la remise sur chaque article
  const dual = c => `<span class="dual"><s>${fmt(c)}</s><b>${fmt(off(c))}</b></span>`;
  const dual2 = (was, now) => `<span class="dual"><s>${fmt(was)}</s><b>${fmt(now)}</b></span>`;
  const esc = s => String(s).replace(/[&<>"]/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch]));

  // État
  const S = {
    step: 1,          // 1 corps (départ), 2-4 rabats, 5 accessoires, 6 récap
    sizeKey: null,
    flaps: [null, null, null],
    extras: [],       // {catId, productId}
    xPhase: 'ask',    // ask | category | product | another
    xCat: null,
    xSel: new Set(),  // produits cochés dans la catégorie ouverte (choix multiple), pas encore ajoutés : « idCatégorie|idProduit »
    claspMode: null,  // fermoirs : 'no' (sans charm), 'yes' (avec un charm), null (question posée)
    added: false,
  };
  const STEP_COUNT = 6;
  // Version PC : mêmes données, écrans pensés pour la largeur
  const DESKQ = window.matchMedia('(min-width: 1024px)');
  const isDesk = () => DESKQ.matches;
  let hover = { size: null, flap: null };  // aperçu au survol (PC)
  let dxCat = null;                         // onglet d'accessoires ouvert (PC)
  const ORD = DATA.i18n.ord;   // texte brut (attributs aria)
  const ORDH = DATA.i18n.ordH; // affichage, en exposant

  const size = () => DATA.sizes.find(s => s.key === S.sizeKey) || DATA.sizes[0];
  const flapById = id => size().flaps.find(f => f.id === id);
  const XS = () => size().extras;
  const extraCat = id => XS().find(c => c.id === id);
  const extraProduct = (catId, pid) => (extraCat(catId) || { products: [] }).products.find(p => p.id === pid);

  // Fermoirs, comme dans le configurateur (demande de Theo, 25/10/2026 : des clientes prenaient un fermoir perforé
  // sans charm, le trou restait vide). Une seule entrée « Fermoirs » demande « avec ou sans charm » :
  // sans → fermoirs non perforés ; avec → fermoirs perforés, puis les charms juste en dessous.
  // Les articles gardent leur vraie catégorie (panier, suivi inchangés). Sans l'une des 3 catégories : rien ne change.
  const CLASPS = 'clasps';
  const byTrack = name => XS().find(c => c.trackName === name);
  function claspGroup() {
    const no = byTrack('Fermoir non perforé'), yes = byTrack('Fermoir perforé'), ch = byTrack('Charms');
    return no && yes && ch && no.products.length && yes.products.length && ch.products.length ? { no, yes, ch } : null;
  }
  // Catégories réelles derrière une entrée de la liste
  const catIdsOf = id => { const g = claspGroup(); return id === CLASPS && g ? [g.no.id, g.yes.id, g.ch.id] : [id]; };
  // Entrées de la liste des accessoires : les 3 catégories de fermoirs et charms n'en font qu'une
  function xEntries() {
    const cats = XS().filter(c => c.products.length);
    const g = claspGroup();
    if (!g) return cats;
    return cats.filter(c => c !== g.yes && c !== g.ch)
      .map(c => c === g.no ? { id: CLASPS, name: t('clasps'), products: [...g.no.products, ...g.yes.products], cover: g.no.cover } : c);
  }
  const inEntry = (id, e) => catIdsOf(id).includes(e.catId);
  // Choix « avec ou sans charm » à l'ouverture des fermoirs, d'après ce qui est déjà choisi (null : question posée)
  function claspModeFrom(list) {
    const g = claspGroup();
    if (!g) return null;
    if (list.some(e => e.catId === g.yes.id || e.catId === g.ch.id)) return 'yes';
    return list.some(e => e.catId === g.no.id) ? 'no' : null;
  }
  // Fermoir perforé sans aucun charm dans la liste : à l'étape accessoires, le bouton reste grisé
  // (« Choisissez d'abord votre charm », demande de Theo) ; au récapitulatif, simple rappel
  function needsCharm(list) {
    const g = claspGroup();
    return !!g && list.some(e => e.catId === g.yes.id) && !list.some(e => e.catId === g.ch.id);
  }
  const charmMissing = () => needsCharm(S.extras);
  // Cases cochées sur la page d'une catégorie (mobile) : « idCatégorie|idProduit »
  const xkey = (catId, pid) => catId + '|' + pid;
  const xsplit = k => { const [catId, productId] = k.split('|'); return { catId, productId }; };

  // Quand on change de taille, on garde les rabats choisis par nom (les visuels changent selon la taille)
  function setSize(key) {
    const prev = S.sizeKey ? size() : null;
    S.sizeKey = key;
    const next = size();
    S.flaps = S.flaps.map((id, i) => {
      if (!id) return null;
      const name = prev && prev.flaps.find(f => f.id === id)?.name;
      return next.flaps.find(f => f.name === name)?.id || null;
    });
    S.extras = S.extras.map(e => {
      const c = prev && prev.extras.find(x => x.id === e.catId);
      const p = c && c.products.find(x => x.id === e.productId);
      const nc = c && next.extras.find(x => x.name === c.name);
      const np = nc && p && nc.products.find(x => x.name === p.name);
      return np ? { catId: nc.id, productId: np.id } : null;
    }).filter(Boolean);
    dxCat = null;
  }

  // Prix d'un rabat pas encore choisi : celui du rabat par défaut du lot.
  // Le lot complet (sac + 3 rabats) est compté dès le départ, pour que le prix ne grimpe pas en cours de route.
  const refFlapPrice = sz => (sz.flaps.find(f => f.id === sz.defaultFlap) || sz.flaps[0]).price;

  // Prix rond du lot (demande de Theo, 31/10/2026) : le « sac + 3 rabats » remisé ligne par ligne est arrondi à l'euro
  // le plus proche, l'écart reporté sur la ligne du sac ; les accessoires gardent leurs centimes. Même calcul dans le
  // panier (bundle.php, funnel_bundle_apply_per_line_discount) : la cliente paie exactement le prix affiché.
  const roundEuro = c => Math.round(c / 100) * 100;
  function coreOf(sz, flapIds) {
    const lines = [sz.price, ...sz.imposed.map(p => p.price),
      ...flapIds.map(id => (id && sz.flaps.find(f => f.id === id)?.price) || refFlapPrice(sz))];
    const exact = lines.reduce((a, c) => a + off(c), 0);
    const now = roundEuro(exact);
    return { orig: lines.reduce((a, c) => a + c, 0), now, delta: now - exact };
  }
  const NO_FLAPS = [null, null, null];

  // Accessoires comptés : ceux ajoutés, et sur la page d'une catégorie, les cases cochées à la place de ceux de cette catégorie
  function effectiveExtras() {
    if (!(S.step === 5 && S.xPhase === 'product')) return S.extras;
    return S.extras.filter(e => !inEntry(S.xCat, e)).concat([...S.xSel].map(xsplit));
  }

  function totals() {
    // Lot (sac + 3 rabats) au prix rond, puis chaque accessoire remisé à part, comme dans le panier
    const core = coreOf(size(), S.flaps);
    let sum = core.orig, now = core.now;
    for (const e of effectiveExtras()) { const p = extraProduct(e.catId, e.productId)?.price || 0; sum += p; now += off(p); }
    return { sum, now, save: sum - now, core };
  }

  // Rabat montré dans l'aperçu selon l'étape
  function previewFlapIndex() {
    if (S.step >= 2 && S.step <= 4) return S.step - 2;
    return 0;
  }

  // flapId === false : sac sans rabat (étape 1, choix de la taille, décision de Theo). Le fermoir, fixé
  // sur le rabat, est masqué avec lui ; la sangle et les anses restent.
  function layersHTML(sz, flapId) {
    const noFlap = flapId === false;
    const layers = [{ src: sz.body, z: 0 }];
    for (const p of sz.imposed) if (p.image && !(noFlap && /fermoir/i.test(p.cat))) layers.push({ src: p.image, z: p.layer });
    const f = noFlap ? null : sz.flaps.find(x => x.id === flapId) || sz.flaps.find(x => x.id === sz.defaultFlap);
    if (f) layers.push({ src: f.image, z: f.layer });
    return layers.sort((a, b) => a.z - b.z).map(l => `<img src="${l.src}" alt="" style="z-index:${l.z}">`).join('');
  }

  // Un seul cadre pour les 3 tailles (l'union de leurs zones utiles, avec de l'air autour) :
  // le Petit paraît plus petit que le Grand, comme en vrai
  const CROP = (() => {
    const PAD = 0.04;
    let x0 = 1, y0 = 1, x1 = 0, y1 = 0;
    for (const sz of DATA.sizes) {
      const c = sz.crop || { x: 0, y: 0, w: 1, h: 1 };
      x0 = Math.min(x0, c.x); y0 = Math.min(y0, c.y);
      x1 = Math.max(x1, c.x + c.w); y1 = Math.max(y1, c.y + c.h);
    }
    // L'air ajouté peut dépasser le canevas : la zone hors canevas reste simplement blanche
    x0 -= PAD; y0 -= PAD; x1 += PAD; y1 += PAD;
    const w = x1 - x0, h = y1 - y0;
    return { x: x0, y: y0, w, h, ratio: (w * DATA.canvasRatio) / h };
  })();

  // Sac composé, recadré sur le cadre commun aux 3 tailles
  function bagFrameHTML(sz, flapId) {
    const c = CROP;
    return `<span class="bagframe" style="aspect-ratio:${c.ratio};--r:${c.ratio}"><span class="canvas" style="width:${100 / c.w}%;height:${100 / c.h}%;left:${-c.x / c.w * 100}%;top:${-c.y / c.h * 100}%">${layersHTML(sz, flapId)}</span></span>`;
  }

  function renderPreview() {
    const desk = isDesk();
    const sz = desk && S.step === 1 && hover.size ? DATA.sizes.find(x => x.key === hover.size) : size();
    const i = previewFlapIndex();
    // Sac nu (sans rabat ni fermoir) à l'étape 1 et à chaque étape de rabat tant que ce rabat n'est pas choisi
    // (décision de Theo, 18/10/2026) : jamais de rabat imposé par défaut dans l'aperçu
    const flapId = S.step <= 1 ? false : desk && S.step >= 2 && S.step <= 4 && hover.flap ? hover.flap : S.flaps[i] || false;
    const c = CROP;
    const stage = root.getElementById('stage');
    stage.style.aspectRatio = String(c.ratio);
    stage.style.setProperty('--r', String(c.ratio));
    const stageKey = sz.key + ':' + flapId;
    if (stage.dataset.key !== stageKey) stage.dataset.key = stageKey, stage.innerHTML = `<div class="canvas" style="width:${100 / c.w}%;height:${100 / c.h}%;left:${-c.x / c.w * 100}%;top:${-c.y / c.h * 100}%">${layersHTML(sz, flapId)}</div>`;
    const top = root.getElementById('topline');
    top.hidden = S.step === 0 || S.step >= 6;
    // À l'étape accessoires, le bandeau ne garde que les étapes
    const lead = S.step === 1
      ? `<span class="lead-title">${t('composeLot', { badge: `<span class="badge">${PCT}</span>` })}</span>`
      : `<span class="badge">${PCT}</span>`;
    top.innerHTML = S.step === 5 ? stepper() : `<div class="row">${lead}${S.sizeKey && S.step > 1 && S.step !== 6 ? `<button type="button" class="recap-link" data-goto="6">${t('seeRecap')}</button>` : ''}</div>${stepper()}`;
    const f = sz.flaps.find(x => x.id === flapId);
    root.getElementById('caption').innerHTML = desk
      ? (S.sizeKey || hover.size ? `${esc(sz.label)} <span class="dim">· ${DIMS[sz.key] || ''}</span>` : t('hoverSizeHere'))
      : esc(S.sizeKey ? (f ? t('captionWithFlap', { size: sz.label, flap: f.name }) : t('captionFlapTodo', { size: sz.label })) : t('captionStart'));
    const sum = root.getElementById('desksum');
    sum.innerHTML = desk && S.step >= 5 ? S.extras.map((e, k) => {
      const p = extraProduct(e.catId, e.productId);
      return `<span class="desk-chip"><img src="${p.thumb}" alt="">${esc(p.name)}<button type="button" data-rm="${k}" aria-label="${esc(t('removeAria', { name: p.name }))}">×</button></span>`;
    }).join('') : '';
    root.getElementById('trio').innerHTML = S.flaps.map((id, k) => {
      const f = flapById(id);
      const cur = S.step === k + 2 ? ' is-current' : '';
      return `<button type="button" data-goto="${k + 2}" class="${cur}" ${S.sizeKey ? '' : 'disabled'}>
        <span class="ph photo">${f ? `<img src="${f.photo || f.thumb}" alt="">` : ''}</span>
        <span class="lbl">${t('flapN', { ord: ORDH[k] })}</span>
        <span class="nm${f ? '' : ' empty'}">${f ? esc(f.name) : t('toChoose')}</span>
      </button>`;
    }).join('');
  }

  function renderBar() {
    const bar = root.getElementById('bar');
    bar.hidden = false;
    wz.classList.add('has-bar');

    let label = t('next'), disabled = false, act = 'next';
    if (S.step === 7) {
      // Lot ajouté : un seul gros bouton, vers le paiement
      bar.innerHTML = `<div class="bar-in done-mode"><a class="btn start next" href="${esc(DATA.urls.checkout)}">${t('checkout')} <svg viewBox="0 0 20 14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M1 7h17M12 1l6 6-6 6"/></svg></a></div>`;
      return;
    }
    if (S.step === 1) disabled = !S.sizeKey;
    else if (S.step <= 4) disabled = !S.flaps[S.step - 2];
    else if (S.step === 5 && S.xPhase === 'product') {
      const had = S.extras.some(e => inEntry(S.xCat, e));
      label = S.xSel.size ? t('addToSelectionN', { n: S.xSel.size }) : had ? t('update') : t('addToSelection');
      act = 'xadd'; disabled = !S.xSel.size && !had;
      if (needsCharm([...S.xSel].map(xsplit))) { label = t('charmFirst'); disabled = true; }
    }
    else if (S.step === 5) {
      label = t('recap');
      if (needsCharm(S.extras)) { label = t('charmFirst'); disabled = true; }
    }
    else {
      label = S.cart === 'loading' ? t('adding') : S.cart === 'added' ? t('added') : t('addToCart');
      act = 'cart'; disabled = !S.flaps.every(Boolean) || S.cart === 'loading';
    }

    let price;
    const xmode = S.step === 5 && S.xPhase === 'product';
    if (xmode) {
      // Page d'une catégorie d'accessoires : le bouton prend la place du prix et annonce le surcoût
      const cur = S.extras.filter(e => inEntry(S.xCat, e)).reduce((a, e) => a + off(extraProduct(e.catId, e.productId).price), 0);
      const next = [...S.xSel].map(xsplit).reduce((a, e) => a + off(extraProduct(e.catId, e.productId).price), 0);
      const delta = next - cur;
      label = `<span class="xl">${label}</span>${delta ? `<small>${t('deltaOnLot', { delta: (delta > 0 ? '+' : '−') + fmt(Math.abs(delta)) })}</small>` : ''}`;
      price = '';
    } else if (S.step === 6) {
      // Récapitulatif : le prix est déjà dans le bloc gris, la barre ne garde que le retour et un très grand « Ajouter au panier »
      price = '';
    } else if (S.step === 1) {
      // Étape 1 : pas de prix ici (il est sur les cartes), juste la taille choisie
      const sz = S.sizeKey ? size() : null;
      price = sz
        ? `<div class="bar-sel"><span class="k">${t('yourSelection')}</span><b>${esc(sz.label)}</b></div>`
        : `<div class="bar-sel"><span class="k">${t('chooseSize')}</span></div>`;
    } else if (!S.sizeKey) {
      const best = DATA.sizes.map(s => coreOf(s, NO_FLAPS)).reduce((a, c) => (c.now < a.now ? c : a));
      price = `<div class="bar-price">
        <div class="row"><span class="off">${PCT}</span><span class="was">${fmt(best.orig)}</span><span class="now">${t('fromPrice', { price: fmt(best.now) })}</span></div>
        <span class="save">${t('bagPlus3')}</span>
      </div>`;
    } else {
      const tot = totals();
      price = `<div class="bar-price">
        <div class="row"><span class="off">${PCT}</span><span class="was">${fmt(tot.sum)}</span><span class="now">${fmt(tot.now)}</span></div>
        <span class="save">${t('saved', { amount: fmt(tot.save) })}</span>
        ${tot.now > 14999 ? `<span class="ship">${t('shipBar')}</span>` : ''}
      </div>`;
    }
    bar.innerHTML = `<div class="bar-in${S.step === 1 ? ' first' : ''}${xmode ? ' xmode' : ''}${S.step === 6 ? ' xmode recap-mode' : ''}">
      <button type="button" class="btn ghost back" data-act="${S.step === 5 && S.xPhase === 'product' ? 'xback' : 'back'}" aria-label="${esc(t('prevStepAria'))}" ${S.step === 1 ? 'hidden' : ''}><svg viewBox="0 0 14 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 3 3 12l8 9"/></svg><span class="long">${t('prev')}</span></button>
      ${price}
      <button type="button" class="btn start next" data-act="${act}" ${disabled ? 'disabled' : ''}>${label}${act === 'next' && S.xPhase !== 'product' ? ' <svg viewBox="0 0 20 14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M1 7h17M12 1l6 6-6 6"/></svg>' : ''}</button>
    </div>`;
  }

  const STEP_LABELS = [t('stepBag'), ...ORDH.map(o => t('flapN', { ord: o })), t('stepAccessories')];

  function stepper() {
    const dots = STEP_LABELS.map((lbl, i) => {
      const n = i + 1;
      const state = n < S.step ? 'done' : n === S.step ? 'current' : '';
      const inner = `<span class="dot">${state === 'done' ? '✓' : n}</span><span class="sl">${lbl}</span>`;
      return `<li class="${state}">${state === 'done'
        ? `<button type="button" class="st" data-goto="${n}" aria-label="${esc(t('backTo', { step: lbl.replace(/<[^>]+>/g, '') }))}">${inner}</button>`
        : `<span class="st" ${state === 'current' ? 'aria-current="step"' : ''}>${inner}</span>`}</li>`;
    }).join('');
    return `<ol class="stepper" aria-label="${esc(t('ariaSteps'))}">${dots}</ol>`;
  }

  // « Anses noires incluses (+0 €) » → « anses noires » ; fermoir précédé de son type
  // Note d'une taille réglée dans le shortcode (ex. « En précommande, livraison fin novembre »)
  const preNote = sz => sz.note ? `<span class="pre-note">${esc(sz.note)}</span>` : '';

  function includedText(sz) {
    return sz.imposed.map(p => {
      const n = p.name.replace(/\s*incluses?\s*/i, ' ').replace(/\(\+?0\s*€\)/, '').trim();
      // en allemand, les noms gardent leur majuscule (« Verschluss Metall-Oval »)
      const lower = DATA.i18n.lang === 'de' ? n : n.charAt(0).toLowerCase() + n.slice(1);
      return /fermoir/i.test(p.cat) ? t('inclClasp', { name: lower }) : lower;
    }).join(t('and'));
  }
  // « Inclus : » suivi des vignettes des produits fournis avec le sac (demande de Theo, 29/10/2026 : plus clair
  // sans avoir à lire). Le texte complet reste en aria-label, chaque nom en infobulle.
  function includedHTML(sz) {
    const label = t('included', { items: '' }).trim();
    const thumbs = sz.imposed.filter(p => p.thumb).map(p => `<span class="inc-ph" title="${esc(p.name)}"><img src="${p.thumb}" alt=""></span>`).join('');
    return `<span class="inc-row" role="img" aria-label="${esc(t('included', { items: includedText(sz) }))}"><span class="inc-k">${esc(label)}</span>${thumbs}</span>`;
  }

  // Dimensions des fiches produit WooCommerce (hauteur × largeur × profondeur)
  const DIMS = DATA.dims; // dimensions des fiches produit WooCommerce (hauteur × largeur × profondeur)

  function stepSize() {
    const opts = DATA.sizes.map(sz => {
      const base = coreOf(sz, NO_FLAPS);
      return `<button type="button" class="opt size-opt" data-size="${sz.key}" aria-pressed="${S.sizeKey === sz.key}">
        <span class="ph layered">${layersHTML(sz, false)}${DIMS[sz.key] ? `<span class="dims" title="${esc(t('dimsTitle'))}">${DIMS[sz.key]}</span>` : ''}</span>
        <span class="nm">${esc(sz.label)}</span>
        ${preNote(sz)}
        <span class="inc">${includedHTML(sz)}</span>
        <span class="lot-price"><span class="lbl">${t('lotBag3')}</span>${dual2(base.orig, base.now)}</span>
      </button>`;
    }).join('');
    return `<div class="fade" style="display:grid;gap:18px">
      <h2 class="step-title">${t('whichSize')}</h2>
      <div class="grid sizes" id="slider">${opts}</div>
    </div>`;
  }

  function stepFlap(k) {
    const sz = size();
    const opts = sz.flaps.map(f => {
      const other = S.flaps.findIndex((id, j) => j !== k && id === f.id);
      const out = f.inStock === false;
      // Un rabat déjà pris pour un autre emplacement, ou épuisé, ne peut pas être choisi
      return `<button type="button" class="opt flap-card${other >= 0 || out ? ' taken' : ''}" data-flap="${f.id}" aria-pressed="${S.flaps[k] === f.id}" ${other >= 0 ? `disabled aria-label="${esc(t('takenAria', { name: f.name, ord: ORD[other] }))}"` : out ? `disabled aria-label="${esc(t('soldOutAria', { name: f.name }))}"` : ''}>
        ${other >= 0 ? `<span class="tag">${ORDH[other]}</span>` : out ? `<span class="tag">${t('soldOut')}</span>` : ''}
        <span class="cut${f.photo ? ' photo' : ''}"><img src="${f.photo || f.thumb}" alt=""></span>
        <span class="nm">${esc(f.name)}</span>
        ${f.price !== refFlapPrice(sz) ? `<span class="pr">${f.price > refFlapPrice(sz) ? '+' : '−'}${fmt(Math.abs(off(f.price) - off(refFlapPrice(sz))))}</span>` : ''}
      </button>`;
    }).join('');
    const titles = ORDH.map(o => t('whichFlap', { ord: o }));
    return `<div class="fade" style="display:grid;gap:12px">
      <h2>${titles[k]}</h2>
      <div class="flap-row-wrap">
        <button type="button" class="row-arrow prev" data-row="-1" aria-label="${esc(t('prevFlapsAria'))}">‹</button>
        <div class="flap-row" id="flaps">${opts}</div>
        <button type="button" class="row-arrow next" data-row="1" aria-label="${esc(t('nextFlapsAria'))}">›</button>
      </div>
    </div>`;
  }

  // ───── Écrans PC ─────
  function deskSize() {
    const opts = DATA.sizes.map(sz => `
      <button type="button" class="dsize" data-size="${sz.key}" aria-pressed="${S.sizeKey === sz.key}">
        ${bagFrameHTML(sz, false)}
        <span><span class="nm">${esc(sz.label)}</span>${preNote(sz)}<span class="dimp">${DIMS[sz.key] || ''}</span><span class="inc">${includedHTML(sz)}</span></span>
        <span class="lp"><span class="lbl">${t('lotBag3')}</span>${(c => dual2(c.orig, c.now))(coreOf(sz, NO_FLAPS))}</span>
      </button>`).join('');
    return `<div class="fade" style="display:grid;gap:18px">
      <h2 class="desk-title">${t('whichSize')}</h2>
      <p class="desk-hint">${t('hoverSize')}</p>
      <div class="dsizes">${opts}</div>
    </div>`;
  }

  function deskFlap(k) {
    const sz = size();
    const opts = sz.flaps.map(f => {
      const other = S.flaps.findIndex((id, j) => j !== k && id === f.id);
      const out = f.inStock === false;
      return `<button type="button" class="opt flap-card${other >= 0 || out ? ' taken' : ''}" data-flap="${f.id}" aria-pressed="${S.flaps[k] === f.id}" ${other >= 0 ? `disabled aria-label="${esc(t('takenAria', { name: f.name, ord: ORD[other] }))}"` : out ? `disabled aria-label="${esc(t('soldOutAria', { name: f.name }))}"` : ''}>
        ${other >= 0 ? `<span class="tag">${ORDH[other]}</span>` : out ? `<span class="tag">${t('soldOut')}</span>` : ''}
        <span class="cut${f.photo ? ' photo' : ''}"><img src="${f.photo || f.thumb}" alt=""></span>
        <span class="nm">${esc(f.name)}</span>
      </button>`;
    }).join('');
    return `<div class="fade" style="display:grid;gap:18px">
      <h2 class="desk-title">${t('whichFlap', { ord: ORDH[k] })}</h2>
      <p class="desk-hint">${t('hoverFlap')}</p>
      <div class="dflaps">${opts}</div>
    </div>`;
  }

  // Accessoires qu'on peut ajouter (prix « dès … » des catégories)
  const buyable = c => c.products.filter(p => !p.included);

  // Carte d'un accessoire. Celui déjà fourni avec le sac (sangle fine, fermoir ovale métal) est grisé,
  // non cliquable, et dit « Déjà dans votre lot » à la place du prix.
  function productCard(p, key, pressed, catId) {
    if (p.included) {
      return `<button type="button" class="opt is-included" disabled aria-label="${esc(t('includedAria', { name: p.name }))}">
        <span class="ph"><img src="${p.thumb}" alt=""></span>
        <span class="nm">${esc(p.name)}</span>
        <span class="pr">${t('alreadyIncluded')}</span>
      </button>`;
    }
    return `
      <button type="button" class="opt" data-${key}="${xkey(catId, p.id)}" aria-pressed="${pressed}">
        <span class="check" aria-hidden="true">✓</span>
        <span class="ph"><img src="${p.thumb}" alt=""></span>
        <span class="nm">${esc(p.name)}</span>
        <span class="pr">${dual(p.price)}</span>
      </button>`;
  }

  const askTitle = () =>t(S.extras.length ? 'askAnother' : 'askMore', { pct: pctHTML });
  const countAcc = k => tn('accessoriesCount', k);

  // Contenu de l'entrée « Fermoirs » : question « avec ou sans charm », puis la liste qui correspond.
  // isOn(catId, pid) : case cochée ; key : attribut des cartes (dxprod sur PC, xprod sur mobile) ; gridCls : grille.
  function claspBody(isOn, key, gridCls) {
    const g = claspGroup();
    const m = S.claspMode;
    const grid = c => `<div class="${gridCls}">${c.products.map(p => productCard(p, key, isOn(c.id, p.id), c.id)).join('')}</div>`;
    // Illustrations (demande de Theo) : cœur noir sans trou / cœur noir perforé avec un charm accroché au trou.
    // Images détourées des cartes ; le charm passe sous le cœur, son anneau sort du trou (positions dans wizard.css).
    const pick = (c, name) => c.products.find(p => p.trackName === name) || c.products[0];
    const charm = g.ch.products.find(p => p.trackName === 'Sunshine - Noir') || pick(g.ch, 'Clé');
    const ill = {
      no: `<img class="h" src="${pick(g.no, 'Coeur noir').thumb}" alt="">`,
      yes: `<img class="c" src="${charm.thumb}" alt=""><img class="h" src="${pick(g.yes, 'Coeur noir - perforé').thumb}" alt="">`,
    };
    const opt = (v, k) => `<button type="button" class="clasp-opt" data-clasp="${v}" aria-pressed="${m === v}">
      <span class="clasp-ph" aria-hidden="true"><span class="clasp-ill is-${v}">${ill[v]}</span></span><b>${t(k)}</b><span>${t(k + 'Hint')}</span></button>`;
    return `<div class="clasp-q"><p class="clasp-title">${t('claspQ')}</p><div class="clasp-opts">${opt('no', 'claspNo')}${opt('yes', 'claspYes')}</div></div>
      ${m === 'no' ? grid(g.no) : ''}
      ${m === 'yes' ? `<p class="clasp-sub">1. ${t('claspStep1')}</p>${grid(g.yes)}<p class="clasp-sub" id="charm-pick">2. ${t('charmPick')}</p>${grid(g.ch)}` : ''}`;
  }
  // Fermoir perforé coché sans charm encore choisi : on descend jusqu'aux charms (14 fermoirs les cachent sinon)
  function toCharmsIfNeeded(catId, chosen, list) {
    const g = claspGroup();
    if (!g || catId !== g.yes.id || !chosen || list.some(e => e.catId === g.ch.id)) return;
    root.getElementById('charm-pick')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function deskExtras() {
    const cats = xEntries();
    if (!dxCat || !cats.some(c => c.id === dxCat)) dxCat = cats[0].id;
    const tabs = cats.map(c => {
      const n = S.extras.filter(e => inEntry(c.id, e)).length;
      return `<button type="button" class="dtab" data-dxcat="${c.id}" aria-pressed="${c.id === dxCat}">
        <img src="${(c.products.find(p => p.id === c.cover) || c.products[0]).thumb}" alt="">${esc(c.name)}${n ? `<span class="cnt">${n}</span>` : ''}
      </button>`;
    }).join('');
    const isOn = (catId, pid) => S.extras.some(e => e.catId === catId && e.productId === pid);
    const prods = dxCat === CLASPS
      ? claspBody(isOn, 'dxprod', 'dprods')
      : `<div class="dprods">${extraCat(dxCat).products.map(p => productCard(p, 'dxprod', isOn(dxCat, p.id), dxCat)).join('')}</div>`;
    return `<div class="fade" style="display:grid;gap:16px">
      <h2 class="desk-title">${askTitle()}</h2>
      <p class="desk-hint">${t('tickHint')}</p>
      <div class="dtabs">${tabs}</div>
      ${prods}
    </div>`;
  }

  function extrasList() {
    if (!S.extras.length) return '';
    return `<div class="recap">${S.extras.map((e, i) => {
      const p = extraProduct(e.catId, e.productId);
      const c = extraCat(e.catId);
      return `<div class="recap-row">
        <span class="ph"><img src="${p.thumb}" alt=""></span>
        <span><span class="k">${esc(c.name)}</span><br><span class="v">${esc(p.name)}</span></span>
        <span class="p">${dual(p.price)}</span>
        <button type="button" class="edit" data-rm="${i}">${t('remove')}</button>
      </div>`;
    }).join('')}</div>`;
  }

  function stepExtras() {
    let body = '';
    if (S.xPhase === 'ask' || S.xPhase === 'another') {
      const q = t(S.xPhase === 'ask' ? 'askMore' : 'askAnother', { pct: PCT });
      const sz = size();
      const cards = S.flaps.map((id, k) => {
        const f = flapById(id);
        return `<div class="opt sel-card">
          ${bagFrameHTML(sz, id)}
          <span class="k">${t('flapN', { ord: ORDH[k] })}</span>
          <span class="nm">${f ? esc(f.name) : t('toChoose')}</span>
        </div>`;
      }).join('') + S.extras.map((e, i) => {
        const p = extraProduct(e.catId, e.productId);
        // Seuls les accessoires se retirent ici ; le sac et les rabats se modifient dans leurs étapes
        return `<div class="opt sel-card">
          <button type="button" class="rm-x" data-rm="${i}" aria-label="${esc(t('removeAria', { name: p.name }))}">×</button>
          <span class="xph" style="aspect-ratio:${CROP.ratio};--r:${CROP.ratio}"><img src="${p.thumb}" alt=""></span>
          <span class="k">${esc(extraCat(e.catId).name)}</span>
          <span class="nm">${esc(p.name)}</span>
        </div>`;
      }).join('');
      // Carrousel des catégories : un clic mène directement au choix du produit
      const cats = xEntries().map(c => `
        <button type="button" class="opt xcat-card" data-xcat="${c.id}" aria-pressed="false">
          <span class="ph"><img src="${(c.products.find(p => p.id === c.cover) || c.products[0]).thumb}" alt=""></span>
          <span class="nm">${esc(c.name)}</span>
          <span class="pr">${(pr => new Set(pr).size > 1 ? t('fromPrice', { price: dual(Math.min(...pr)) }) : dual(Math.min(...pr)))(buyable(c).map(p => p.price))}</span>
        </button>`).join('');
      body = `<p class="eyebrow">${S.extras.length ? t('selectionPlus', { count: countAcc(S.extras.length) }) : t('yourSelection')}</p>
        <div class="grid sizes sel-slider" id="slider">${cards}</div>
        <h2 class="xq">${q}</h2>
        <div class="xcats">${cats}</div>`;
    } else if (S.xPhase === 'category') {
      const cats = xEntries().map(c => `
        <button type="button" class="opt cat-opt" data-xcat="${c.id}" aria-pressed="false">
          <span class="ph"><img src="${c.products[0].thumb}" alt=""></span>
          <span><span class="nm">${esc(c.name)}</span><br><span class="pr">${t('fromPrice', { price: dual(Math.min(...buyable(c).map(p => p.price))) })}</span></span>
        </button>`).join('');
      body = `<h2>${t('whichAccessory')}</h2><div class="grid cats">${cats}</div>
        <div><button type="button" class="btn ghost" data-x="cancel">${t('cancel')}</button></div>`;
    } else if (S.xPhase === 'product') {
      const isOn = (catId, pid) => S.xSel.has(xkey(catId, pid));
      if (S.xCat === CLASPS) {
        body = `<h2>${esc(t('clasps'))}</h2>${claspBody(isOn, 'xprod', 'grid products')}`;
      } else {
        const c = extraCat(S.xCat);
        const opts = c.products.map(p => productCard(p, 'xprod', isOn(c.id, p.id), c.id)).join('');
        body = `<div style="display:grid;gap:2px"><h2>${esc(c.name)}</h2><p class="lede" style="font-size:13px">${t('multiple')}</p></div>
          <div class="grid products">${opts}</div>`;
      }
    }
    const gap = S.xPhase === 'ask' || S.xPhase === 'another' ? 12 : 18;
    return `<div class="fade" style="display:grid;gap:${gap}px">${body}</div>`;
  }

  function stepRecap() {
    const sz = size();
    const tot = totals();
    const flapRows = S.flaps.map((id, k) => {
      const f = flapById(id);
      return `<div class="recap-row">
        <span class="ph photo">${f ? `<img src="${f.photo || f.thumb}" alt="">` : ''}</span>
        <span><span class="k">${t('flapN', { ord: ORDH[k] })}</span><br><span class="v">${f ? esc(f.name) : `<span class="imposed">${t('notChosen')}</span>`}</span></span>
        <span class="p">${f ? dual(f.price) : '–'}</span>
        <button type="button" class="edit" data-goto="${k + 2}">${t('edit')}</button>
      </div>`;
    }).join('');
    const imposedSum = sz.imposed.reduce((a, p) => a + p.price, 0);
    const extraRows = S.extras.map((e, i) => {
      const p = extraProduct(e.catId, e.productId);
      return `<div class="recap-row">
        <span class="ph photo"><img src="${p.thumb}" alt=""></span>
        <span><span class="k">${esc(t('accessoryOf', { cat: extraCat(e.catId).name }))}</span><br><span class="v">${esc(p.name)}</span></span>
        <span class="p">${dual(p.price)}</span>
        <button type="button" class="edit" data-rm="${i}">${t('remove')}</button>
      </div>`;
    }).join('');
    const complete = S.flaps.every(Boolean);
    return `<div class="fade" style="display:grid;gap:18px">
      <h2 class="recap-title">${t('recapTitle', { pct: pctHTML })}${S.extras.length ? ` <span class="more">+ ${countAcc(S.extras.length)}</span>` : ''}</h2>
      <div class="totals">
        <div class="was">${t('totalPrice', { price: `<s>${fmt(tot.sum)}</s>` })}</div>
        <div class="now">${t('lotPrice', { price: fmt(tot.now) })}</div>
        <div class="save">${t('youSave', { amount: `<b>${fmt(tot.save)}</b>` })}</div>
        ${tot.now > 14999 ? `<div class="ship">${t('shipRecap')}</div>` : ''}
      </div>
      ${charmMissing() ? `<div class="charm-missing"><span>${t('charmMissing')}</span><button type="button" class="edit" data-xopen="1">${t('charmCta')}</button></div>` : ''}
      <div class="recap">
        <div class="recap-row">
          <span class="ph layered">${layersHTML(sz, S.flaps[0])}</span>
          <span><span class="k">${t('stepBag')}</span><br><span class="v">${esc(sz.label)}</span>${preNote(sz)}<br><span class="imposed">${includedHTML(sz)}</span></span>
          <span class="p">${dual2(sz.price + imposedSum, off(sz.price) + sz.imposed.reduce((a, p) => a + off(p.price), 0) + tot.core.delta)}</span>
          <button type="button" class="edit" data-goto="1">${t('edit')}</button>
        </div>
        ${flapRows}
        ${extraRows}
      </div>
      <div style="display:flex;justify-content:center;margin-bottom:28px"><button type="button" class="btn ghost" data-goto="5" data-xreset="1">${t('addAccessory', { pct: PCT })}</button></div>
      ${complete ? '' : `<p class="lede" style="font-size:13px">${t('chooseFlapsHint')}</p>`}
    </div>`;
  }

  // Confirmation : le lot est dans le panier (le panier latéral s'est ouvert par-dessus)
  function stepDone() {
    const sz = size();
    const tot = totals();
    // Détail en toutes lettres : on ne voit qu'un rabat sur l'image, les 3 doivent être nommés
    const row = (thumb, k, v) => `<li><span class="ph${thumb.layered ? ' layered' : ' photo'}">${thumb.html}</span><span><span class="k">${k}</span><span class="v">${v}</span></span></li>`;
    const rows = [
      row({ layered: true, html: layersHTML(sz, S.flaps[0]) }, t('stepBag'), esc(sz.label)),
      ...S.flaps.map((id, k) => { const f = flapById(id); return row({ html: f ? `<img src="${f.photo || f.thumb}" alt="">` : '' }, t('flapN', { ord: ORDH[k] }), f ? esc(f.name) : '–'); }),
      ...S.extras.map(e => { const p = extraProduct(e.catId, e.productId); return row({ html: `<img src="${p.thumb}" alt="">` }, esc(t('accessoryOf', { cat: extraCat(e.catId).name })), esc(p.name)); }),
    ].join('');
    return `<div class="fade done-screen">
      <span class="done-check" aria-hidden="true">✓</span>
      <h2 class="done-title">${t('doneTitle')}</h2>
      ${preNote(sz)}
      <ul class="done-list">${rows}</ul>
      <p class="done-save">${t(tot.now > 14999 ? 'doneSaveShip' : 'doneSave', { amount: `<b>${fmt(tot.save)}</b>` })}</p>
      <div class="done-links">
        <button type="button" class="link-btn" data-act="again">${t('another')}</button>
        <a class="link-btn" href="${esc(DATA.urls.shop)}">${t('continueShopping')}</a>
      </div>
    </div>`;
  }

  // Mesure : chaque étape atteinte envoie un événement (une fois par visite) à Google Analytics et Meta.
  // Les noms se lisent tels quels dans un entonnoir GA4 (Explorer > Entonnoir), sans dimension à créer.
  const STEP_EVENTS = { 1: 'lot_etape_taille', 2: 'lot_etape_rabat1', 3: 'lot_etape_rabat2', 4: 'lot_etape_rabat3', 5: 'lot_etape_accessoires', 6: 'lot_etape_recap' };
  const sentSteps = new Set();
  function trackStep() {
    const ev = STEP_EVENTS[S.step];
    if (!ev || sentSteps.has(ev)) return;
    sentSteps.add(ev);
    const sz = S.sizeKey ? size() : null;
    hooks.track(ev, sz ? { taille: sz.trackLabel } : {});
  }

  function render() {
    // La ligne de rabats est reconstruite à chaque rendu : on garde sa position
    const oldRow = root.getElementById('flaps');
    const keepScroll = oldRow && oldRow.dataset.step === String(S.step) ? oldRow.scrollLeft : null;
    // Pareil pour les carrousels (sélection, accessoires) quand on reste sur le même écran
    const screenKey = S.step + ':' + S.xPhase;
    const keepCarousels = render.lastKey === screenKey
      ? { slider: root.getElementById('slider')?.scrollLeft, xcats: root.querySelector('.xcats')?.scrollLeft }
      : null;
    render.lastKey = screenKey;
    trackStep();
    const desk = isDesk();
    const shell = root.querySelector('.shell');
    shell.classList.toggle('desk', desk);
    shell.classList.toggle('flap-mode', !desk && S.step >= 2 && S.step <= 4);
    shell.classList.toggle('intro-mode', !desk && S.step === 0);
    shell.classList.toggle('size-mode', !desk && (S.step === 1 || S.step >= 5));
    // Mobile : le trio n'apparaît qu'au récapitulatif. PC : dès qu'on choisit les rabats, comme résumé du lot
    root.getElementById('trio').hidden = desk ? S.step < 2 : S.step < 6;
    if (desk && S.step === 5) S.xPhase = S.extras.length ? 'another' : 'ask';
    let html = '';
    if (S.step <= 1) html += desk ? deskSize() : stepSize();
    else if (S.step <= 4) html += desk ? deskFlap(S.step - 2) : stepFlap(S.step - 2);
    else if (S.step === 5) html += desk ? deskExtras() : stepExtras();
    else if (S.step === 6) html += stepRecap();
    else html += stepDone();
    root.getElementById('panel').innerHTML = html;
    if (keepCarousels) root.querySelector('#panel > .fade')?.classList.remove('fade');
    renderPreview();
    renderBar();
    layoutVars();
    initSlider();
    if (keepCarousels) {
      const sl = root.getElementById('slider'), xc = root.querySelector('.xcats');
      if (sl && keepCarousels.slider != null) sl.scrollLeft = keepCarousels.slider;
      if (xc && keepCarousels.xcats != null) xc.scrollLeft = keepCarousels.xcats;
    }
    const row = root.getElementById('flaps');
    if (row) {
      row.dataset.step = String(S.step);
      if (keepScroll !== null) row.scrollLeft = keepScroll;
      else {
        const sel = row.querySelector('[aria-pressed="true"]');
        if (sel) row.scrollLeft = sel.offsetLeft - row.offsetLeft - 40;
      }
    }
  }

  // Le slider est reconstruit à chaque rendu : on le replace sur la carte choisie
  function initSlider() {
    const el = root.getElementById('slider');
    if (!el) return;
    const cards = [...el.children];
    const dots = [...root.querySelectorAll('.slider-dots button')];
    const sel = cards.findIndex(c => c.getAttribute('aria-pressed') === 'true');
    if (sel > 0) el.scrollLeft = cards[sel].offsetLeft - 2;
    const sync = () => {
      const w = cards[0].offsetWidth + 12;
      const i = Math.min(cards.length - 1, Math.round(el.scrollLeft / w));
      dots.forEach((d, k) => d.setAttribute('aria-current', String(k === i)));
    };
    el.addEventListener('scroll', sync, { passive: true });
    sync();
  }

  // Mesures réelles du bandeau du haut, du bloc rabats et de la barre du bas, pour placer le sac entre eux
  function layoutVars() {
    const rootStyle = wz.style;
    const bar = root.getElementById('bar');
    const top = root.getElementById('topline');
    const panel = root.getElementById('panel');
    rootStyle.setProperty('--bar-h', (bar.hidden ? 0 : bar.offsetHeight) + 'px');
    const hdr = hooks.headerH();
    rootStyle.setProperty('--hdr', hdr + 'px');
    // Bas réel du bandeau des étapes à l'écran (il peut y avoir une barre au-dessus, ex. page « parcours seul »)
    rootStyle.setProperty('--top-end', Math.max(hdr, top.hidden ? 0 : top.getBoundingClientRect().bottom) + 8 + 'px');
    if (S.step >= 2 && S.step <= 4) rootStyle.setProperty('--flaps-h', panel.offsetHeight + 'px');
    // Hauteur occupée en bas de l'écran par le parcours (barre + bloc des rabats collé sur mobile),
    // pour que les boutons flottants du site (langue…) se placent au-dessus
    const fixedPanel = !isDesk() && S.step >= 2 && S.step <= 4;
    hooks.bottomInset((bar.hidden ? 0 : bar.offsetHeight) + (fixedPanel ? panel.offsetHeight : 0));
  }
  window.addEventListener('resize', layoutVars);

  function trackAccessory(catId, pid) {
    const c = extraCat(catId), p = extraProduct(catId, pid);
    if (c && p) hooks.track('lot_accessoire_ajoute', { categorie: c.trackName, produit: p.trackName, valeur: off(p.price) / 100 });
  }

  function refresh() {
    renderPreview();
    renderBar();
    layoutVars();
  }

  function go(step) {
    S.step = Math.max(1, Math.min(6, step));
    render();
    // Chaque étape s'ouvre en haut de page
    hooks.scrollTop();
  }

  // Survol PC : la taille ou le rabat s'affiche sur le grand sac, sans rien sélectionner
  root.addEventListener('mouseover', ev => {
    if (!isDesk()) return;
    const sizeBtn = ev.target.closest('[data-size]'), flapBtn = ev.target.closest('[data-flap]:not([disabled])');
    const next = { size: sizeBtn ? sizeBtn.dataset.size : null, flap: flapBtn ? flapBtn.dataset.flap : null };
    if (next.size !== hover.size || next.flap !== hover.flap) { hover = next; renderPreview(); }
  });
  DESKQ.addEventListener('change', () => { hover = { size: null, flap: null }; render(); });

  root.addEventListener('click', ev => {
    if (ev.target.closest('.done-mode a')) hooks.track('lot_finaliser', {});
    const b = ev.target.closest('button');
    if (!b || b.disabled) return;
    const d = b.dataset;
    if (d.row) {
      const row = root.getElementById('flaps');
      row.scrollBy({ left: +d.row * row.clientWidth * 0.8, behavior: 'smooth' });
      return;
    }
    if (d.slide) {
      const el = root.getElementById('slider');
      el.scrollTo({ left: el.children[+d.slide].offsetLeft - 2, behavior: 'smooth' });
      return;
    }
    if (d.goto) { if (d.xreset) { S.xPhase = S.extras.length ? 'another' : 'ask'; } go(+d.goto); return; }
    if (d.act === 'next') { go(S.step + 1); return; }
    if (d.act === 'back') { if (S.step === 5) S.xPhase = 'ask'; go(S.step - 1); return; }
    if (d.act === 'cart') {
      hooks.addToCart(S, size(), effectiveExtras(), renderBar, () => {
        const sz = size(), tot = totals();
        hooks.trackAddToCart({
          value: tot.now / 100, lot: sz.lotName, slug: sz.slug, taille: sz.trackLabel,
          rabats: S.flaps.map(id => flapById(id)?.trackName).filter(Boolean).join(', '), accessoires: S.extras.length,
        });
        S.step = 7; render(); hooks.scrollTop();
      });
      return;
    }
    if (d.act === 'again') {
      hooks.track('lot_nouveau', {});
      // Nouveau lot : on repart de zéro (le précédent est déjà dans le panier)
      Object.assign(S, { step: 1, sizeKey: null, flaps: [null, null, null], extras: [], xPhase: 'ask', xCat: null, xSel: new Set(), claspMode: null, cart: undefined });
      dxCat = null;
      render(); hooks.scrollTop(); return;
    }
    if (d.size) {
      setSize(d.size);
      root.querySelectorAll('[data-size]').forEach(x => x.setAttribute('aria-pressed', String(x.dataset.size === d.size)));
      refresh(); return;
    }
    if (d.flap) {
      S.flaps[S.step - 2] = d.flap;
      root.querySelectorAll('[data-flap]').forEach(x => x.setAttribute('aria-pressed', String(x.dataset.flap === d.flap)));
      refresh(); return;
    }
    // Fermoirs : « avec ou sans charm »
    // Changer de côté retire ce qui était coché de l'autre côté (sinon un fermoir perforé caché grise le bouton)
    if (d.clasp) {
      const g = claspGroup();
      const other = d.clasp === 'no' ? [g.yes.id, g.ch.id] : [g.no.id];
      S.claspMode = d.clasp;
      S.xSel = new Set([...S.xSel].filter(k => !other.includes(xsplit(k).catId)));
      if (isDesk()) S.extras = S.extras.filter(e => !other.includes(e.catId));
      render(); return;
    }
    // Rappel du récapitulatif : ouvre les fermoirs côté « avec un charm »
    if (d.xopen) {
      S.claspMode = 'yes';
      if (isDesk()) dxCat = CLASPS;
      else { S.xCat = CLASPS; S.xSel = new Set(S.extras.filter(e => inEntry(CLASPS, e)).map(e => xkey(e.catId, e.productId))); S.xPhase = 'product'; }
      go(5);
      // directement aux charms, après le retour en haut de page de go()
      setTimeout(() => root.getElementById('charm-pick')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 350);
      return;
    }
    if (d.x === 'back-cats') { S.xPhase = S.extras.length ? 'another' : 'ask'; render(); return; }
    if (d.x === 'no') { S.xPhase = 'another'; go(6); return; }
    if (d.x === 'cancel') { S.xPhase = S.extras.length ? 'another' : 'ask'; render(); return; }
    if (d.xcat) {
      S.xCat = d.xcat;
      S.xSel = new Set(S.extras.filter(e => inEntry(d.xcat, e)).map(e => xkey(e.catId, e.productId)));
      if (d.xcat === CLASPS) S.claspMode = claspModeFrom(S.extras);
      S.xPhase = 'product'; render(); hooks.scrollTop(); return;
    }
    if (d.dxcat) { dxCat = d.dxcat; if (dxCat === CLASPS) S.claspMode = claspModeFrom(S.extras); render(); return; }
    if (d.dxprod) {
      const { catId, productId } = xsplit(d.dxprod);
      const i = S.extras.findIndex(e => e.catId === catId && e.productId === productId);
      if (i >= 0) S.extras.splice(i, 1); else { S.extras.push({ catId, productId }); trackAccessory(catId, productId); }
      b.setAttribute('aria-pressed', String(i < 0));
      // Compteurs des onglets et titre, sans recharger les images
      root.querySelectorAll('[data-dxcat]').forEach(t => {
        const n = S.extras.filter(e => inEntry(t.dataset.dxcat, e)).length;
        let cnt = t.querySelector('.cnt');
        if (n && !cnt) { cnt = document.createElement('span'); cnt.className = 'cnt'; t.appendChild(cnt); }
        if (cnt) n ? (cnt.textContent = n) : cnt.remove();
      });
      const title = root.querySelector('#panel .desk-title');
      if (title) title.innerHTML = askTitle();
      refresh();
      toCharmsIfNeeded(catId, i < 0, S.extras);
      return;
    }
    if (d.xprod) {
      S.xSel.has(d.xprod) ? S.xSel.delete(d.xprod) : S.xSel.add(d.xprod);
      b.setAttribute('aria-pressed', String(S.xSel.has(d.xprod)));
      refresh();
      toCharmsIfNeeded(xsplit(d.xprod).catId, S.xSel.has(d.xprod), [...S.xSel].map(xsplit));
      return;
    }
    if (d.act === 'xadd') {
      const had = new Set(S.extras.filter(e => inEntry(S.xCat, e)).map(e => xkey(e.catId, e.productId)));
      for (const k of S.xSel) if (!had.has(k)) { const e = xsplit(k); trackAccessory(e.catId, e.productId); }
      S.extras = S.extras.filter(e => !inEntry(S.xCat, e)).concat([...S.xSel].map(xsplit));
      S.xPhase = S.extras.length ? 'another' : 'ask'; render(); hooks.scrollTop(); return;
    }
    if (d.act === 'xback') { S.xPhase = S.extras.length ? 'another' : 'ask'; render(); hooks.scrollTop(); return; }
    if (d.rm) { S.extras.splice(+d.rm, 1); if (!S.extras.length && S.step === 5) S.xPhase = 'ask'; render(); return; }
  });

  render();
}
